/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, Prompt, Redirect, useHistory, useLocation } from 'react-router-dom';
import {
  extractTenDigitCode,
  peekExportClassifyHandoff,
  setExportClassifyHandoffCode,
} from 'utils/exportClassifyHandoff';
import InputForm from 'components/TradeHelper/InputForm';
import FollowupQuestionsDisplay from 'components/TradeHelper/FollowupQuestionsDisplay';
import { useWebSocketFetcher } from 'hooks/useWebSocketFetcher';
import { iFollowupItem } from 'interfaces/iFollowupItem';
import { iStreamedResponse } from 'interfaces/iStreamedResponse';
import StreamDisplay from 'components/TradeHelper/StreamDisplay';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Modal,
  Tooltip,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import { GlobalContext } from 'providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import CasesAPI from 'api/CasesAPI';
import CreditsAPI from 'api/CreditsAPI';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { LoadingFeedback } from 'components/Common/LoadingModal';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import ECCNForm from 'components/TradeHelper/ECCNForm';
import StreamEDisplay from 'components/TradeHelper/StreamEDisplay';
import { ECCNcheck } from 'api/PyAPIConnect';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import 'resources/styles/chatkit-classify.css';
// HtsClassificationChat is no longer rendered anywhere — the Claude managed
// agent is now the only Classify chat. It remains the home of the shared
// patch/formatting helpers and types that this page, HtsClassificationChatClaude,
// ChatUI and ClassifyResultsComp all import, so the module stays.
import {
  HtsUiPatch,
  CrossRulingLink,
  tryParseStreamEnvelope,
} from 'components/TradeHelper/HtsClassificationChat';
import HtsClassificationChatClaude, {
  HtsClassificationChatHandle as HtsClassificationChatClaudeHandle,
  HtsClassificationSaveSnapshot,
} from 'components/TradeHelper/HtsClassificationChatClaude';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;
const classificationLeaveWarningStorageKey =
  'classificationLeaveWarningDismissed';

const isHtsPlaceholderValue = (value?: string | null) =>
  /^<[^>]+>$/.test(value?.trim() || '');

const cleanHtsValue = (value?: string | null) => {
  const trimmed = value?.trim() || '';
  return isHtsPlaceholderValue(trimmed) ? '' : trimmed;
};

type ClassificationPathDetailMap = Record<string, string>;

const normalizeHtsDigits = (value?: string | null) =>
  cleanHtsValue(value).replace(/\D/g, '');

const extractGraphProgressDetail = (streamItem: string) => {
  const envelope = tryParseStreamEnvelope(streamItem);
  const htsDigits = normalizeHtsDigits(envelope?.hts_code);
  const message = cleanHtsValue(envelope?.message);

  if (![4, 6, 8].includes(htsDigits.length) || !message) {
    return null;
  }

  // GRAPH_PROGRESS messages are rendered in the chat with an HTS heading.
  // The path card only needs the selected node's description.
  const detail = message
    .replace(/^###\s*HTS Number:[^\r\n]*(?:\r?\n)+/i, '')
    .trim();

  return detail
    ? {
        htsDigits: htsDigits,
        detail: detail,
      }
    : null;
};

const collectClassificationPathDetails = (streamItems: string[]) =>
  streamItems.reduce<ClassificationPathDetailMap>((details, streamItem) => {
    const progressDetail = extractGraphProgressDetail(streamItem);
    if (progressDetail) {
      details[progressDetail.htsDigits] = progressDetail.detail;
    }
    return details;
  }, {});

const Classification: React.FC = () => {
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const location = useLocation<{ exportHandoff?: { productDescription?: string } }>();
  const logAppAction = useLogAppAction();

  // Export Workflow → Classify tool round trip (UX redesign slice B; additive). The product
  // description arrives once in route state (auto-sent as the first message, then the route state
  // is cleared so a refresh doesn't resend it). The return banner keys off the sessionStorage
  // handoff record instead, so it survives a refresh or "New Classification".
  const [exportHandoffMessage] = useState<string>(() =>
    String(location.state?.exportHandoff?.productDescription || '').trim(),
  );
  const [fromExportWorkflow] = useState<boolean>(() => Boolean(peekExportClassifyHandoff()));

  // Export Workflow handoff (slice B): drop the route state once it has been read, so a refresh
  // never re-sends the description.
  useEffect(() => {
    if (exportHandoffMessage) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      });
    }
  }, []);
  
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const { streamData, isStreaming, connectWebSocket } =
    useWebSocketFetcher(apiUrl);

  const [userProductDesc, setUserProductDesc] = useState<string>('');
  const [userECCN, setUserECCN] = useState<any>([]);
  const [country, setCountry] = useState<string>('');
  const [followupItems, setFollowupItems] = useState<iFollowupItem[]>([]);
  const [clfId, setClfId] = useState<string | null>(null);
  const [cmdtDesc, setCmdtDesc] = useState<string | null>(null);
  const [HTSCode, setHTSCode] = useState<string | null>(null);
  const [explanatoryNotes, setExplanatoryNotes] = useState<string | null>(null);
  const [sectionNotes, setSectionNotes] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<number>(0);
  const [currentEStage, setCurrentEStage] = useState<number>(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandedCross, setIsExpandedCross] = useState(false);
  const [isExpandedSecNotes, setIsExpandedSecNotes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savingTicket, setSavingTicket] = useState(false);
  const [ticketId, setTicketID] = useState('');
  const [autoTab, setAutoTab] = useState<string>('HTS Classification');
  const [owner, setOwner] = useState('');
  const [crossLinks, setCrossLinks] = useState<CrossRulingLink[]>([]);

  const [material, setMaterial] = useState<string>('');
  const [intendedUse, setIntendedUse] = useState<string>('');
  const [physicalState, setPhysicalState] = useState<string>('');

  // License Checking
  const [userHasClassify, setUserHasClassify] = useState(true);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);

  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [disableLeaveWarning, setDisableLeaveWarning] = useState(
    () => localStorage.getItem(classificationLeaveWarningStorageKey) === 'true',
  );
  const allowNavigationRef = useRef(false);
  const leaveSaveInProgressRef = useRef(false);

  const requiredTiers = ['pro', 'business', 'enterprise'];
  const isInteractionDisabled =
    !userHasClassify && !isWhiteLabel && cur_user?.bite_super_admin !== true;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  const pyApiClient = useMemo(
    () =>
      axios.create({
        baseURL: apiUrl,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    [apiUrl],
  );

  const [chatkitStreamData, setChatkitStreamData] = useState<string[]>([]);
  const [chatkitThreadId, setChatkitThreadId] = useState<string | null>(null);
  const [chatkitThinking, setChatkitThinking] = useState(false);
  const [chatkitError, setChatkitError] = useState<string | null>(null);
  const htsClassificationChatClaudeRef =
    useRef<HtsClassificationChatClaudeHandle | null>(null);
  const chatkitThreadIdRef = useRef<string | null>(null);
  const hydratedChatkitThreadRef = useRef<string | null>(null);
  const lastLivePatchAtRef = useRef<number>(0);
  // One-shot guard: a poll batch can deliver the FINAL_CLASSIFICATION stage
  // more than once for the same completed run (handleHtsLivePatch fires once
  // per new stream item, not once per turn), so this prevents double-charging.
  // Reset alongside the other per-thread refs above.
  const htsCreditChargedRef = useRef(false);

  // Latest patch's workflow stage — drives the "Under Review" (proposed,
  // not yet confirmed) styling on the Classification Path panel.
  const [reviewStage, setReviewStage] = useState<string>('');
  const [finalSummaryText, setFinalSummaryText] = useState<string>('');
  const [finalReasoningTrace, setFinalReasoningTrace] = useState<string[]>([]);
  const [finalFactsReliedOn, setFinalFactsReliedOn] = useState<string[]>([]);
  const [finalLegalBasisSummary, setFinalLegalBasisSummary] =
    useState<string>('');
  const [finalConfidenceNote, setFinalConfidenceNote] = useState<string>('');
  const [finalChapterNumber, setFinalChapterNumber] = useState<string>('');
  const [finalChapterDescription, setFinalChapterDescription] =
    useState<string>('');
  const [classificationPathDetails, setClassificationPathDetails] =
    useState<ClassificationPathDetailMap>({});

  const checkForClassify = () => {
    if (!requiredTiers.includes(cur_user.license)) {
      setUserHasClassify(false);
    }
  };

  useEffect(() => {
    checkForClassify();
    setOwner(cur_user.basics?.email || '');
  }, []);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  // useEffect(() => {
  //   if (!useChatKitForHTS || !chatkitThreadId) {
  //     return;
  //   }

  //   let cancelled = false;

  //   const fetchLiveState = async () => {
  //     try {
  //       const response = await pyApiClient.get(
  //         `/classify/hts/live-state/${chatkitThreadId}`,
  //       );

  //       if (cancelled) return;

  //       const state = response?.data;
  //       if (!state) return;

  //       if (Array.isArray(state.stream_items)) {
  //         setChatkitStreamData(state.stream_items);
  //       }

  //       if (state.latest) {
  //         handleHtsLivePatch({
  //           stream_item: '',
  //           latest: state.latest,
  //         });
  //       }
  //     } catch (error) {
  //       if (!cancelled) {
  //         console.error('Failed to fetch HTS thread state', error);
  //       }
  //     }
  //   };

  //   fetchLiveState();

  //   const id = window.setInterval(
  //     fetchLiveState,
  //     chatkitThinking ? 3000 : 12000,
  //   );

  //   return () => {
  //     cancelled = true;
  //     window.clearInterval(id);
  //   };
  // }, [useChatKitForHTS, chatkitThreadId, chatkitThinking, pyApiClient]);

  // Process stream
  useEffect(() => {
    streamData.forEach((chunk) => {
      try {
        const classificationReq: iStreamedResponse = JSON.parse(chunk);
        const {
          followup_items,
          options, // Extract this from the chunk
          clf_id,
          hts_code,
          cmdt_desc,
          explanatory_notes,
          section_chapter_notes,
          cross_data_links,
        } = classificationReq;
        if (hts_code) {
          setHTSCode(hts_code);
        }
        if (cmdt_desc) {
          setCmdtDesc(cmdt_desc);
        }
        if (explanatory_notes) {
          setExplanatoryNotes(explanatory_notes);
        }
        if (section_chapter_notes) {
          setSectionNotes(section_chapter_notes);
        }
        if (cross_data_links) {
          const normalizedLinks = (cross_data_links as any[]).map((item) => {
            if (typeof item === 'string') {
              return {
                title: item,
                url: item,
                relevance_percent: null,
                relevance_note: null,
              };
            }

            return {
              title: item?.title ?? item?.url ?? 'Untitled ruling',
              url: item?.url ?? '',
              relevance_percent:
                typeof item?.relevance_percent === 'number'
                  ? item.relevance_percent
                  : null,
              relevance_note: item?.relevance_note ?? null,
            };
          });

          setCrossLinks(normalizedLinks.filter((item) => item.url));
        }

        if (clf_id && clf_id.trim() !== '') {
          setClfId((prev) => (prev === clf_id ? prev : clf_id));
        }

        if (followup_items && followup_items.length > 0) {
          setFollowupItems((prev) => {
            const existingQuestions = new Set(
              prev.map((item) => item.question),
            );
            const newItems = followup_items.filter(
              (item) => !existingQuestions.has(item.question),
            );
            return [...prev, ...newItems];
          });
        }
      } catch (err) {
        console.warn('Invalid JSON chunk in useEffect:', chunk, err);
      }
    });
  }, [streamData]);

  // Handle default stages
  useEffect(() => {
    if (followupItems.length > 0) {
      setCurrentStage(2);
    }
    if (cmdtDesc && HTSCode && explanatoryNotes) {
      setCurrentStage(3);
    }
  }, [followupItems, cmdtDesc, HTSCode]);

  const submitClassification = async (
    product_desc: string,
    mat: string,
    use: string,
    state: string,
    followups: iFollowupItem[],
  ) => {
    if (isInteractionDisabled) {
      return;
    }
    const clf = {
      clf_id: clfId,
      product_desc: product_desc,
      material: mat, // New field
      intended_use: use, // New field
      physical_state: state, // New field
      followup_items: followups,
    };
    connectWebSocket('/ws/classify', clf);
  };

  //chatkit update
  const handleHtsLivePatch = useCallback((patch: {
    stream_item: string;
    latest: {
      stage?: string | null;
      clf_id?: string | null;
      hts_code?: string | null;
      cmdt_desc?: string | null;
      explanatory_notes?: string | null;
      section_chapter_notes?: string | null;
      cross_data_links?: CrossRulingLink[];
      followup_items?: Array<{ question: string; answer?: string }>;
      options?: string[];
      user_facing_message?: string | null;
      summary_text?: string | null;
      reasoning_trace?: string[];
      facts_relied_on?: string[];
      legal_basis_summary?: string | null;
      confidence_note?: string | null;
      chapter_number?: string | null;
      chapter_description?: string | null;
      // final_widget?: any;
    };
  }) => {
    const latest = patch?.latest;

      if (!latest) return;

      lastLivePatchAtRef.current = Date.now();

      const stage = latest.stage || '';
      if (stage) {
        setReviewStage(stage);
      }

      
    // Right-pane stream behavior mimics the old stage-driven flow:
    // - chapter suggestion may show briefly
    // - chapter confirmed replaces prior proposal
    // - graph progress replaces prior graph prompt/history
    // - final classification replaces prior graph progress/history
    // Append-or-replace happens exactly once per patch so non-replacement
    // stages don't render the same stream entry twice.
    if (patch?.stream_item) {
      const pathDetails = collectClassificationPathDetails([patch.stream_item]);
      if (Object.keys(pathDetails).length > 0) {
        setClassificationPathDetails((current) => ({
          ...current,
          ...pathDetails,
        }));
      }

      const replaceStages = new Set([
        'CHAPTER_CONFIRMED',
        'GRAPH_PROGRESS',
        'FINAL_CLASSIFICATION',
        'FINAL_CLASSIFICATION_ACCEPTED',
        'FINAL_REVIEW',
      ]);

        if (replaceStages.has(stage)) {
          setChatkitStreamData([patch.stream_item]);
        } else {
          setChatkitStreamData((prev) => [...prev, patch.stream_item]);
        }
      }

      if (latest.clf_id && String(latest.clf_id).trim() !== '') {
        const nextClfId = String(latest.clf_id);
        setClfId((prev) => (prev === nextClfId ? prev : nextClfId));
      }

    const cleanHtsCode = cleanHtsValue(latest.hts_code);
    const cleanCmdtDesc = cleanHtsValue(latest.cmdt_desc);

    if (cleanHtsCode) {
      setHTSCode(cleanHtsCode);
    }

    if (cleanCmdtDesc) {
      setCmdtDesc(cleanCmdtDesc);
    }

      if (latest.explanatory_notes) {
        setExplanatoryNotes(latest.explanatory_notes);
      }

      if (latest.section_chapter_notes) {
        setSectionNotes(latest.section_chapter_notes);
      }

      if (latest.cross_data_links) {
        const normalizedLinks = latest.cross_data_links.map((item: any) => {
          if (typeof item === 'string') {
            return {
              title: item,
              url: item,
              relevance_percent: null,
              relevance_note: null,
            };
          }

          return {
            title: item?.title ?? item?.url ?? 'Untitled ruling',
            url: item?.url ?? '',
            relevance_percent:
              typeof item?.relevance_percent === 'number'
                ? item.relevance_percent
                : null,
            relevance_note: item?.relevance_note ?? null,
          };
        });

        setCrossLinks(normalizedLinks.filter((item) => item.url));
      }

      if (latest.followup_items) {
        setFollowupItems(latest.followup_items as any);
      }

    // New final explanation fields
    const cleanSummaryText =
      cleanHtsValue(latest.summary_text) ||
      cleanHtsValue(latest.user_facing_message);

    if (cleanSummaryText) {
      setFinalSummaryText(cleanSummaryText);
    }

    if (latest.reasoning_trace) {
      setFinalReasoningTrace(latest.reasoning_trace.map(cleanHtsValue).filter(Boolean));
    }

    if (latest.facts_relied_on) {
      setFinalFactsReliedOn(latest.facts_relied_on.map(cleanHtsValue).filter(Boolean));
    }

    const cleanLegalBasisSummary = cleanHtsValue(
      latest.legal_basis_summary,
    );
    if (cleanLegalBasisSummary) {
      setFinalLegalBasisSummary(cleanLegalBasisSummary);
    }

    const cleanConfidenceNote = cleanHtsValue(latest.confidence_note);
    if (cleanConfidenceNote) {
      setFinalConfidenceNote(cleanConfidenceNote);
    }

      if (latest.chapter_number) {
        setFinalChapterNumber(latest.chapter_number);
      }

      if (latest.chapter_description) {
        setFinalChapterDescription(latest.chapter_description);
      }

      // Optional graceful final widget
      // if (latest.final_widget) {
      //   setFinalClassificationWidget(latest.final_widget);
      // }
    },
    [],
  );

  useEffect(() => {
    if (!chatkitThreadId) {
      hydratedChatkitThreadRef.current = null;
      return;
    }

    if (hydratedChatkitThreadRef.current === chatkitThreadId) {
      return;
    }

    hydratedChatkitThreadRef.current = chatkitThreadId;

    let cancelled = false;
    const requestStartedAt = Date.now();

    const hydrateLiveState = async () => {
      try {
        const response = await pyApiClient.get(
          `/classify/hts/live-state/${chatkitThreadId}`,
        );

        if (cancelled) return;

        // If a client-tool patch arrived after this request started,
        // do not let older hydrated state overwrite newer local state.
        if (lastLivePatchAtRef.current > requestStartedAt) {
          return;
        }

        const state = response?.data;
        if (!state) return;

        if (Array.isArray(state.stream_items)) {
          setChatkitStreamData(state.stream_items);
          setClassificationPathDetails(
            collectClassificationPathDetails(state.stream_items),
          );
        }

        if (state.latest) {
          handleHtsLivePatch({
            stream_item: '',
            latest: state.latest,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to hydrate HTS thread state', error);
        }
      }
    };

    hydrateLiveState();

    // eslint-disable-next-line consistent-return -- standard effect-cleanup
    // pattern: early exits above intentionally return nothing, this path
    // returns the cleanup function.
    return () => {
      cancelled = true;
    };
  }, [chatkitThreadId, pyApiClient, handleHtsLivePatch]);

  const handleSubmit = async (
    desc: string,
    mat: string,
    use: string,
    state: string,
  ) => {
    if (desc !== '') {
      logAppAction('classification:run');
      setUserProductDesc(desc);
      setMaterial(mat);
      setIntendedUse(use);
      setPhysicalState(state);

      setCurrentStage(1);
      setIsLoading(true);
      try {
        await submitClassification(desc, mat, use, state, followupItems);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    } else {
      enqueueSnackbar('Please input something to classify', {
        variant: 'error',
      });
    }
  };

  const handleESubmit = async (inputValue: string, countr: string) => {
    if (isInteractionDisabled) {
      return;
    }
    if (inputValue !== '' && countr !== '') {
      setCurrentEStage(1);
      setIsLoading(true);
      setUserECCN([]);
      try {
        const new_case = {
          eccn_full: inputValue,
          country_raw: countr,
        };
        const resp = await ECCNcheck(new_case);
        if (true) {
          setUserECCN(resp);
          enqueueSnackbar('Successfully received controls', {
            variant: 'success',
          });
          setIsLoading(false);
        } else {
        }
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
      enqueueSnackbar(
        'Please input the ECCN and Export Country to receive results.',
        { variant: 'error' },
      );
    }
  };

  const handleFollowupSubmit = (responses: iFollowupItem[]) => {
    if (isInteractionDisabled) {
      return;
    }
    if (userProductDesc !== '') {
      setIsLoading(true);
      setFollowupItems(responses);
      // Persist the initial triage inputs during follow-up rounds
      submitClassification(
        userProductDesc,
        material,
        intendedUse,
        physicalState,
        responses,
      );
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const openTicketModal = () => {
    if (isInteractionDisabled) {
      return;
    }
    setModalOpen(true);
  };

  const resetHtsClassificationState = useCallback(() => {
    setCurrentStage(0);
    setClfId(null);
    setHTSCode('');
    setCmdtDesc('');
    setFollowupItems([]);
    setUserProductDesc('');
    setMaterial('');
    setIntendedUse('');
    setPhysicalState('');
    setTicketID('');
    setIsLoading(false);
    setIsExpanded(false);
    setIsExpandedCross(false);
    setIsExpandedSecNotes(false);

    // New ChatKit-backed state
    setExplanatoryNotes('');
    setSectionNotes('');
    setCrossLinks([]);

    setChatkitStreamData([]);
    setChatkitThinking(false);
    setChatkitError(null);

    setFinalSummaryText('');
    setFinalReasoningTrace([]);
    setFinalFactsReliedOn([]);
    setFinalLegalBasisSummary('');
    setFinalConfidenceNote('');
    setFinalChapterNumber('');
    setFinalChapterDescription('');
    setClassificationPathDetails({});
    setReviewStage('');
    // setFinalClassificationWidget(null);
  }, []);

  const clearHtsClassificationSession = useCallback(() => {
    resetHtsClassificationState();
    chatkitThreadIdRef.current = null;
    hydratedChatkitThreadRef.current = null;
    lastLivePatchAtRef.current = 0;
    htsCreditChargedRef.current = false;
    setChatkitThreadId(null);
  }, [resetHtsClassificationState]);

  const handleHtsThreadChange = useCallback(
    (threadId: string | null) => {
      const previousThreadId = chatkitThreadIdRef.current;

      if (previousThreadId && previousThreadId !== threadId) {
        resetHtsClassificationState();
        hydratedChatkitThreadRef.current = null;
        lastLivePatchAtRef.current = 0;
        htsCreditChargedRef.current = false;
      }

      chatkitThreadIdRef.current = threadId;
      setChatkitThreadId(threadId);
    },
    [resetHtsClassificationState],
  );

  const handleHtsNewChat = useCallback(() => {
    clearHtsClassificationSession();
  }, [clearHtsClassificationSession]);

  const handleReset = () => {
    if (isInteractionDisabled) {
      return;
    }
    logAppAction('classification:new');
    if (autoTab === 'HTS Classification') {
      clearHtsClassificationSession();

      const newChatRequest =
        htsClassificationChatClaudeRef.current?.startNewChat();
      if (newChatRequest) {
        newChatRequest.catch((error: any) => {
          const detail =
            error?.message || 'Unable to start a new classification chat.';
          setChatkitError(detail);
        });
      }
    } else {
      setCurrentEStage(0);
      setTicketID('');
    }
  };

  // crossLinks come from live API state and could contain unsafe URLs (e.g.
  // javascript:). Only render anchors for http/https links.
  const safeCrossLinks = crossLinks.filter((cross_data_link) => {
    try {
      const { protocol } = new URL(cross_data_link.url);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  });

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleTabSwitch = (newTab: string) => {
    if (isInteractionDisabled) {
      return;
    }
    switch (newTab) {
      case 'HTS Classification':
        setAutoTab('HTS Classification');
        window.sessionStorage.setItem('autoTab', 'HTS Classification');
        break;
      case 'Export Control Classification Number':
        setAutoTab('Export Control Classification Number');
        window.sessionStorage.setItem(
          'autoTab',
          'Export Control Classification Number',
        );
        break;
      default:
        setAutoTab('HTS Classification');
    }
  };

  const handleTicket = async (values: any) => {
    if (isInteractionDisabled) {
      return;
    }
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });

    if (resp?.success) {
      setTicketID(resp.result._id);
      enqueueSnackbar('Successfully saved work', { variant: 'success' });
      shareEmail.forEach((email) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          values.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSavingTicket(false);
      setModalOpen(false);
      setNewTicketName('');
      if (leaveSaveInProgressRef.current) {
        leaveSaveInProgressRef.current = false;
        continueToPendingLocation();
      }
    } else if (resp) {
      const error_msg =
        'Error saving, please try again and contact the BITE Team if the problem persist';
      enqueueSnackbar(error_msg, { variant: 'error' });
      setSavingTicket(false);
      setModalOpen(false);
    }
  };

  const saveTicket = async (name: string) => {
    if (isInteractionDisabled) {
      return;
    }
    logAppAction('classification:save');
    setSavingTicket(true);
    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    // The managed-agent chat feeds handleHtsLivePatch into chatkitStreamData.
    const effectiveStreamData = chatkitStreamData;
    const componentSnapshot: HtsClassificationSaveSnapshot | null =
      htsClassificationChatClaudeRef.current?.getSaveSnapshot() || null;
    const snapshotThreadId =
      componentSnapshot?.threadId || chatkitThreadIdRef.current;
    const fallbackStreamItems = chatkitStreamData
      .filter((item): item is string => typeof item === 'string')
      .slice(-100)
      .map((item) =>
        item.length <= 4000
          ? item
          : `${item.slice(0, 4000)}... [truncated]`,
      );
    const fallbackHtsCode = cleanHtsValue(HTSCode);
    const fallbackCmdtDesc = cleanHtsValue(cmdtDesc);
    const fallbackSummaryText = cleanHtsValue(finalSummaryText);
    const fallbackLegalBasisSummary = cleanHtsValue(
      finalLegalBasisSummary,
    );
    const fallbackConfidenceNote = cleanHtsValue(finalConfidenceNote);
    const fallbackReasoningTrace = finalReasoningTrace
      .map(cleanHtsValue)
      .filter(Boolean)
      .slice(0, 20);
    const fallbackFactsReliedOn = finalFactsReliedOn
      .map(cleanHtsValue)
      .filter(Boolean)
      .slice(0, 20);
    const fallbackLatest = {
      stage: 'FINAL_CLASSIFICATION',
      hts_code: fallbackHtsCode || null,
      cmdt_desc: fallbackCmdtDesc || null,
      summary_text: fallbackSummaryText || null,
      legal_basis_summary: fallbackLegalBasisSummary || null,
      confidence_note: fallbackConfidenceNote || null,
      reasoning_trace: fallbackReasoningTrace,
      facts_relied_on: fallbackFactsReliedOn,
      chapter_number: cleanHtsValue(finalChapterNumber) || null,
      chapter_description: cleanHtsValue(finalChapterDescription) || null,
    };
    const fallbackFinalReport: NonNullable<
      HtsClassificationSaveSnapshot['finalReport']
    > | null = fallbackHtsCode
      ? {
          version: 1,
          source: 'publish_final_classification',
          threadId: snapshotThreadId,
          savedAt: new Date().toISOString(),
          payload: {
            thread_id: snapshotThreadId,
            product_desc: cleanHtsValue(userProductDesc) || null,
            hts_code: fallbackHtsCode,
            cmdt_desc: fallbackCmdtDesc || null,
            summary_text: fallbackSummaryText || null,
            legal_basis_summary: fallbackLegalBasisSummary || null,
            confidence_note: fallbackConfidenceNote || null,
            reasoning_trace: fallbackReasoningTrace,
            facts_relied_on: fallbackFactsReliedOn,
            chapter_number: cleanHtsValue(finalChapterNumber) || null,
            chapter_description:
              cleanHtsValue(finalChapterDescription) || null,
          },
          generated: fallbackLatest,
          streamItem: fallbackStreamItems[fallbackStreamItems.length - 1] || '',
        }
      : null;
    const chatkitSnapshot: HtsClassificationSaveSnapshot | null =
      componentSnapshot
        ? {
            ...componentSnapshot,
            threadId: snapshotThreadId,
            observed: {
              messages: componentSnapshot.observed.messages,
              streamItems:
                componentSnapshot.observed.streamItems.length > 0
                  ? componentSnapshot.observed.streamItems
                  : fallbackStreamItems,
            },
            finalReport:
              componentSnapshot.finalReport || fallbackFinalReport,
          }
        : {
            version: 1,
            source: 'hts-classification-managed-agent',
            threadId: snapshotThreadId,
            capturedAt: new Date().toISOString(),
            observed: {
              messages: [],
              streamItems: fallbackStreamItems,
            },
            finalReport: fallbackFinalReport,
          };
    const query: any = {
      owner: ownerQuery,
      ticketName: name,
      lastUpdate: formatted_dt,
      docType: 'Classification',
      sharedWith: shareEmail,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      classificationValue: userProductDesc,
      followUps: followupItems,
      aiStream: effectiveStreamData,
      exNotes: explanatoryNotes,
      chapterNotes: sectionNotes,
      crossRulings: crossLinks,
      commodity: {
        commodityName: cmdtDesc,
        harmonizedScheduleCode: HTSCode,
        material: material,
        intendedUse: intendedUse,
        physicalState: physicalState,
      },
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'saved classification',
        timeStamp: Date.now(),
      },
    };

    if (chatkitSnapshot) {
      query.htsChatHistory = {
        version: 1,
        source: chatkitSnapshot.source,
        threadId: chatkitSnapshot.threadId,
        savedAt: new Date().toISOString(),
        observed: chatkitSnapshot.observed,
        // Managed-agent threads have no external ChatKit transcript to fetch. The
        // ticket viewers fall back to observed.messages when this is unavailable.
        chatkitThread: {
          available: false,
          reason:
            'Managed-agent threads do not have an external ChatKit transcript; using client-observed messages instead.',
        },
      };

      if (chatkitSnapshot.finalReport) {
        query.htsFinalReport = chatkitSnapshot.finalReport;
      }
    }

    handleTicket(query);
  };

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]"]/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: JSX.Element[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };

  const safeCmdtDesc = cleanHtsValue(cmdtDesc);
  const safeHtsCode = cleanHtsValue(HTSCode);
  const safeHtsDigits = normalizeHtsDigits(safeHtsCode);
  const safeFinalSummaryText = cleanHtsValue(finalSummaryText);
  const canSaveClassification =
    Boolean(
      safeCmdtDesc &&
      safeHtsCode &&
      safeCmdtDesc.length > 0 &&
      safeHtsCode.length > 0,
    ) &&
    !isInteractionDisabled;

  const hasUnsavedClassificationWork = Boolean(
    !isInteractionDisabled &&
      !ticketId &&
      (userProductDesc ||
        safeCmdtDesc ||
        safeHtsCode ||
        chatkitStreamData.length > 0 ||
        followupItems.length > 0 ||
        currentEStage > 0 ||
        userECCN.length > 0),
  );
  const shouldWarnOnLeave =
    !disableLeaveWarning &&
    hasUnsavedClassificationWork &&
    !isModalOpen &&
    !isDeleteModalOpen;

  const classificationPathSteps = [
    {
      label: 'Chapter',
      value: finalChapterNumber || (safeHtsCode ? safeHtsCode.slice(0, 2) : ''),
      detail: finalChapterDescription || safeCmdtDesc || '',
    },
    {
      label: 'Heading',
      value:
        safeHtsCode && safeHtsCode.length >= 4 ? safeHtsCode.slice(0, 4) : '',
      detail:
        classificationPathDetails[safeHtsDigits.slice(0, 4)] ||
        safeCmdtDesc ||
        '',
    },
    {
      label: 'Subheading',
      value:
        safeHtsCode && safeHtsCode.length >= 6 ? safeHtsCode.slice(0, 7) : '',
      detail:
        classificationPathDetails[safeHtsDigits.slice(0, 6)] ||
        safeCmdtDesc ||
        '',
    },
    {
      label: 'US Tariff',
      value:
        safeHtsCode && safeHtsCode.length >= 10 ? safeHtsCode.slice(0, 10) : '',
      detail:
        classificationPathDetails[safeHtsDigits.slice(0, 8)] ||
        safeCmdtDesc ||
        '',
    },
    {
      label: 'Statistical Suffix',
      value: safeHtsCode && safeHtsCode.length >= 12 ? safeHtsCode : '',
      // summary_text explains why the complete ten-digit HTS number applies,
      // so it belongs with the statistical suffix rather than the eight-digit
      // US tariff step. facts_relied_on is often only a generic product fact
      // (for example, "Product is identified as a metal shim") and is not a
      // useful substitute for the final-code rationale.
      detail: safeFinalSummaryText || safeCmdtDesc || '',
    },
  ];

  useEffect(() => {
    if (!shouldWarnOnLeave) {
      return undefined;
    }

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [shouldWarnOnLeave]);

  const navigateToPendingLocation = useCallback(() => {
    if (!pendingLocation) {
      return;
    }

    history.push({
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    });
  }, [history, pendingLocation]);

  const continueToPendingLocation = useCallback(() => {
    allowNavigationRef.current = true;
    navigateToPendingLocation();
    window.setTimeout(() => {
      allowNavigationRef.current = false;
      setPendingLocation(null);
    }, 0);
  }, [navigateToPendingLocation]);

  const exportHandoffCode =
    ['FINAL_CLASSIFICATION', 'FINAL_CLASSIFICATION_ACCEPTED'].includes(reviewStage)
      ? extractTenDigitCode(cleanHtsValue(HTSCode))
      : null;

  const handleCopyExportHandoffCode = async () => {
    if (!exportHandoffCode) return;
    try {
      await navigator.clipboard.writeText(exportHandoffCode);
      enqueueSnackbar('Code copied', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Could not copy the code. Select it and copy it instead.', {
        variant: 'warning',
      });
    }
  };

  // Back to the Export Workflow: the code (when final) rides along in the handoff record so the
  // paste card arrives pre-filled. The user's intent is explicit, so the leave guard is bypassed.
  const handleBackToExportWorkflow = () => {
    if (exportHandoffCode) {
      setExportClassifyHandoffCode(exportHandoffCode);
    }
    allowNavigationRef.current = true;
    history.push('/export-page');
    window.setTimeout(() => {
      allowNavigationRef.current = false;
    }, 0);
  };

  const handleBlockedNavigation = (nextLocation: any) => {
    if (allowNavigationRef.current) {
      return true;
    }

    if (!shouldWarnOnLeave) {
      return true;
    }

    setPendingLocation(nextLocation);
    setIsLeaveModalOpen(true);
    return false;
  };

  const handleSaveResultFromLeaveModal = () => {
    leaveSaveInProgressRef.current = true;
    setIsLeaveModalOpen(false);
    openTicketModal();
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    continueToPendingLocation();
  };

  const handleLeaveAndOptOut = () => {
    localStorage.setItem(classificationLeaveWarningStorageKey, 'true');
    setDisableLeaveWarning(true);
    handleLeaveWithoutSaving();
  };

  const renderLeaveModal = () => (
    <>
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
      <Dialog
        className='dialog_box'
        open={isLeaveModalOpen}
        onClose={() => {
          setIsLeaveModalOpen(false);
          setPendingLocation(null);
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave Classification?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => {
              setIsLeaveModalOpen(false);
              setPendingLocation(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this page will discard your current classification
              progress. Use Save Result to store this work before leaving.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={handleLeaveWithoutSaving}
                type='button'
              >
                <div>Leave Without Saving</div>
              </button>
              <button
                className='btn-new-automate'
                disabled={!canSaveClassification || savingTicket}
                onClick={handleSaveResultFromLeaveModal}
                type='button'
              >
                <div>Save Result</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleLeaveAndOptOut}
                type='button'
              >
                <div>Leave + Don&apos;t Show Again</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  const renderClassificationModals = () => (
    <>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DeleteCommonModal
          handleClose={() => setIsDeleteModalOpen(false)}
          title='Delete search?'
          paragraph='This will permanently remove this search from your group list.'
        />
      </Modal>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <CreateTicketWizard
          cur_user={cur_user}
          handleSaveTicket={saveTicket}
          newTicketName={newTicketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={savingTicket}
        />
      </Modal>
    </>
  );

  // The Claude managed-agent chat is the only HTS chat now, so this redesigned
  // shell is unconditional for the HTS tab. Previously it was gated on an env
  // flag that, when unset, silently fell through to the un-redesigned ChatKit
  // widget below — which is why local builds appeared to lose the redesign.
  if (autoTab === 'HTS Classification') {
    return (
      <>
        <section className='bite-classify-redesign Home-HeaderDown'>
          <div className='bite-classify-redesign-header'>
            <div>
              <h1>Classification</h1>
              <p>
                Describe your product and answer follow-up questions to arrive
                at a USHTS code.
              </p>
            </div>
            <div className='bite-classify-redesign-actions'>
              <button
                type='button'
                className='bite-classify-secondary-btn'
                onClick={handleReset}
              >
                New Classification
              </button>
              {!isWhiteLabel && ticketId && (
                <Link
                  to={`/monitor/ticket/${ticketId}`}
                  className='bite-classify-primary-btn'
                >
                  View Saved
                </Link>
              )}
              {!isWhiteLabel && !ticketId && (
                <button
                  type='button'
                  className='bite-classify-primary-btn'
                  disabled={!canSaveClassification || savingTicket}
                  onClick={openTicketModal}
                >
                  Save Result
                </button>
              )}
            </div>
          </div>
          {fromExportWorkflow && (
            <div
              className={`bite-classify-export-banner${exportHandoffCode ? ' is-ready' : ''}`}
              role='status'
            >
              <div className='bite-classify-export-banner-text'>
                <strong>
                  {exportHandoffCode
                    ? 'Your HTS code is ready for the Export Workflow'
                    : 'Classifying for the Export Workflow'}
                </strong>
                <span>
                  {exportHandoffCode
                    ? 'Copy it, go back, and paste it into the Schedule B / HTS step.'
                    : 'When the 10-digit HTS code is ready, it appears here so you can take it back.'}
                </span>
              </div>
              {exportHandoffCode && (
                <div className='bite-classify-export-banner-code'>
                  <span>HTS code</span>
                  <strong>{exportHandoffCode}</strong>
                  <Tooltip title='Copy HTS code' placement='top'>
                    <button
                      type='button'
                      className='bite-classify-export-banner-copy'
                      onClick={handleCopyExportHandoffCode}
                      aria-label={`Copy HTS code ${exportHandoffCode}`}
                    >
                      <ContentCopyOutlinedIcon aria-hidden='true' />
                    </button>
                  </Tooltip>
                </div>
              )}
              <div className='bite-classify-export-banner-actions'>
                <button
                  type='button'
                  className='bite-classify-primary-btn'
                  onClick={handleBackToExportWorkflow}
                >
                  Back to Export Workflow
                </button>
              </div>
            </div>
          )}
          {!isWhiteLabel && (
            <RequiredLicense
              userLicense={normalizedLicense}
              minimumLicense='pro'
            />
          )}
          <div className='bite-classify-advisory'>
            <span aria-hidden='true'>i</span>
            <p>
              The 10-digit HTS classification provided is for general, educational and planning purposes only and not for purposes of filing and entry. Unless you are the importer of record, the specific tariff classification to be applied to a specific entry of merchandise is to be determined, supervised and controlled by the licensed customs broker. Users other than the importer of record must consult a licensed customs broker as classification for Customs entry purposes can only be determined by a licensed customs broker.
            </p>
          </div>

          <div className='bite-classify-redesign-grid'>
            <section className='bite-classify-chat-card'>
              <div className='bite-classify-card-header'>
                <div className='bite-classify-card-title'>
                  <span className='bite-classify-bot-icon' aria-hidden='true'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M7 9H17M8 15H16M9 5V3M15 5V3M6 20H18C19.1046 20 20 19.1046 20 18V8C20 6.89543 19.1046 6 18 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20Z'
                        stroke='currentColor'
                        strokeWidth='1.8'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </span>
                  <h2>USHTS Assistant</h2>
                </div>
                <div
                  className={`bite-classify-status${
                    !chatkitError &&
                    !chatkitThinking &&
                    reviewStage === 'FINAL_REVIEW'
                      ? ' under-review'
                      : ''
                  }`}
                >
                  <span />
                  {chatkitError
                    ? 'Error'
                    : chatkitThinking
                    ? 'Responding'
                    : reviewStage === 'FINAL_REVIEW'
                    ? 'Under Review'
                    : 'Idle'}
                </div>
              </div>

              <div className='bite-classify-chat-body'>
                {isInteractionDisabled ? (
                  <div className='classify-results'>
                    Upgrade your plan to use HTS Classification.
                  </div>
                ) : (
                  <HtsClassificationChatClaude
                    ref={htsClassificationChatClaudeRef}
                    apiUrl={apiUrl}
                    showToolbar={false}
                    initialMessage={exportHandoffMessage || undefined}
                    initialMessageTag={
                      exportHandoffMessage ? 'Sent from Export Workflow' : undefined
                    }
                    onThreadChange={handleHtsThreadChange}
                    onNewChat={handleHtsNewChat}
                    onResponseStart={() => {
                      setChatkitThinking(true);
                      setChatkitError(null);
                    }}
                    onResponseEnd={() => setChatkitThinking(false)}
                    onError={(message) => setChatkitError(message)}
                    onLivePatch={handleHtsLivePatch}
                  />
                )}
              </div>
            </section>

            <aside className='bite-classify-path-card'>
              <div className='bite-classify-path-header'>
                <h2>Classification Path</h2>
                {reviewStage === 'FINAL_REVIEW' && (
                  <span className='bite-classify-path-status under-review'>
                    Under Review
                  </span>
                )}
              </div>
              <div className='bite-classify-path-list'>
                {classificationPathSteps.map((step, index) => {
                  const isPopulated = Boolean(step.value);
                  const isUnderReview =
                    isPopulated && reviewStage === 'FINAL_REVIEW';
                  const isComplete = isPopulated && !isUnderReview;
                  return (
                    <div
                      className={`bite-classify-path-step${
                        isComplete ? ' complete' : ''
                      }${isUnderReview ? ' under-review' : ''}`}
                      key={step.label}
                    >
                      <div className='bite-classify-path-marker'>
                        <span />
                        {index < classificationPathSteps.length - 1 && <i />}
                      </div>
                      <div className='bite-classify-path-item'>
                        <div className='bite-classify-path-item-heading'>
                          <span>{step.label}</span>
                          <strong>{step.value || 'Pending'}</strong>
                        </div>
                        {step.detail ? (
                          <p>{step.detail}</p>
                        ) : (
                          <p className='bite-classify-path-awaiting'>
                            Awaiting information...
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </aside>
          </div>

          <div className='bite-classify-supporting-row'>
            <section className='bite-classify-support-card'>
              <h3>CROSS Rulings</h3>
              {safeCrossLinks.length > 0 ? (
                <div className='bite-classify-cross-links'>
                  {safeCrossLinks.map((item, index) => (
                    <div
                      key={`${item.url}-${index}`}
                      className='bite-classify-cross-link-card'
                    >
                      <a
                        href={item.url}
                        rel='noopener noreferrer'
                        target='_blank'
                        className='bite-classify-cross-link-title'
                      >
                        {item.title || item.url}
                      </a>

                      {typeof item.relevance_percent === 'number' && (
                        <div className='bite-classify-cross-link-relevance'>
                          Relevance: {item.relevance_percent}%
                        </div>
                      )}

                      {item.relevance_note && (
                        <div className='bite-classify-cross-link-note'>
                          {item.relevance_note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className='bite-classify-path-awaiting'>
                  Awaiting information...
                </p>
              )}
            </section>

            <section className='bite-classify-support-card'>
              <h3>Section &amp; Chapter Notes</h3>
              {sectionNotes ? (
                <div className='bite-classify-note-content'>
                  {formatExplanatoryNote(sectionNotes)}
                </div>
              ) : (
                <p className='bite-classify-path-awaiting'>
                  Awaiting information...
                </p>
              )}
            </section>
          </div>
        </section>
        {renderClassificationModals()}
        {renderLeaveModal()}
      </>
    );
  }

  return (
    <>
      <section
        className='home-content-body Home-HeaderDown hts-classification-page'
        style={{ minHeight: 0 }}
      >
        <Grid container>
          <Grid item xs={12}>
            <div className='bill-row hts-classification-row'>
              <div className='bill-containers hts-classification-left-pane'>
                {!isWhiteLabel && (
                  <>
                    <div className='bill-container-header'>
                      <div className='bill-container-header-bread-crumbs'>
                        <div className='bill-container-header-bread-crumbs-tabs'>
                          <Link to='/'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                              cursor={'pointer'}
                            >
                              <path
                                d='M7.5 17.5V11.3333C7.5 10.8666 7.5 10.6333 7.59083 10.455C7.67072 10.2982 7.79821 10.1707 7.95501 10.0908C8.13327 9.99999 8.36662 9.99999 8.83333 9.99999H11.1667C11.6334 9.99999 11.8667 9.99999 12.045 10.0908C12.2018 10.1707 12.3293 10.2982 12.4092 10.455C12.5 10.6333 12.5 10.8666 12.5 11.3333V17.5M9.18141 2.30333L3.52949 6.69927C3.15168 6.99312 2.96278 7.14005 2.82669 7.32405C2.70614 7.48704 2.61633 7.67065 2.56169 7.86588C2.5 8.08627 2.5 8.32558 2.5 8.80421V14.8333C2.5 15.7667 2.5 16.2335 2.68166 16.59C2.84144 16.9036 3.09641 17.1585 3.41002 17.3183C3.76654 17.5 4.23325 17.5 5.16667 17.5H14.8333C15.7668 17.5 16.2335 17.5 16.59 17.3183C16.9036 17.1585 17.1586 16.9036 17.3183 16.59C17.5 16.2335 17.5 15.7667 17.5 14.8333V8.80421C17.5 8.32558 17.5 8.08627 17.4383 7.86588C17.3837 7.67065 17.2939 7.48704 17.1733 7.32405C17.0372 7.14005 16.8483 6.99312 16.4705 6.69927L10.8186 2.30333C10.5258 2.07562 10.3794 1.96177 10.2178 1.918C10.0752 1.87938 9.92484 1.87938 9.78221 1.918C9.62057 1.96177 9.47418 2.07562 9.18141 2.30333Z'
                                stroke='#4895EF'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </Link>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M6 12L10 8L6 4'
                              stroke='var(--card-text)'
                              strokeWidth='1.33333'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                          <div className='bc-title'>{autoTab}</div>
                        </div>
                      </div>
                    </div>
                    <RequiredLicense
                      userLicense={normalizedLicense}
                      minimumLicense='pro'
                    />
                  </>
                )}
                <div
                  className={`bill-container-body hts-classification-left-body ${
                    isExpanded || isExpandedCross ? 'expanded' : ''
                  }`}
                  style={{ alignItems: 'flex-start', gap: '10px' }}
                >
                  <div className='screening-tabs-container'>
                    {!isWhiteLabel && (
                      <div className='screening-tabs-inner'>
                        <div
                          className={`screening-tab-btn${
                            autoTab === 'HTS Classification' ? '-selected' : ''
                          }`}
                          onClick={() => handleTabSwitch('HTS Classification')}
                        >
                          <div
                            className={`screening-tab-btn-text${
                              autoTab === 'HTS Classification'
                                ? '-selected'
                                : ''
                            }`}
                          >
                            {autoTab === 'HTS Classification' ? (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 18 18'
                                fill='none'
                                style={{ marginRight: '5px' }}
                              >
                                <path
                                  d='M15.75 15.75L11.2501 11.25M12.75 7.5C12.75 10.3995 10.3995 12.75 7.5 12.75C4.6005 12.75 2.25 10.3995 2.25 7.5C2.25 4.6005 4.6005 2.25 7.5 2.25C10.3995 2.25 12.75 4.6005 12.75 7.5Z'
                                  stroke='#4895EF'
                                  strokeWidth='1.5'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 18 18'
                                fill='none'
                                style={{ marginRight: '5px' }}
                              >
                                <path
                                  d='M15.75 15.75L11.2501 11.25M12.75 7.5C12.75 10.3995 10.3995 12.75 7.5 12.75C4.6005 12.75 2.25 10.3995 2.25 7.5C2.25 4.6005 4.6005 2.25 7.5 2.25C10.3995 2.25 12.75 4.6005 12.75 7.5Z'
                                  stroke='var(--card-text)'
                                  strokeWidth='1.5'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            )}
                            HTS Classification
                          </div>
                        </div>
                      </div>
                    )}
                    <div className='module-header-title-conts'>
                      <Tooltip
                        title={'Clear'}
                        placement='bottom-start'
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              display: 'flex',
                              padding:
                                'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              alignSelf: 'stretch',
                              borderRadius: 'var(--radius-md, 8px)',
                              background:
                                'var(--Background-200, var(--bg-colors))',
                              color: 'var(--text-prim25)',
                              maxWidth: '700px',
                              '& .MuiTooltip-arrow': {
                                color:
                                  'var(--Background-200, var(--bg-colors))',
                              },
                            },
                          },
                        }}
                      >
                        <div className='audit-btn-star' onClick={handleReset}>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                          >
                            <path
                              d='M17.5003 8.33333C17.5003 8.33333 15.8295 6.05685 14.4721 4.69854C13.1148 3.34022 11.239 2.5 9.16699 2.5C5.02486 2.5 1.66699 5.85786 1.66699 10C1.66699 14.1421 5.02486 17.5 9.16699 17.5C12.5862 17.5 15.4711 15.2119 16.3739 12.0833M17.5003 8.33333V3.33333M17.5003 8.33333H12.5003'
                              stroke='var(--card-text)'
                              strokeWidth='1.66667'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                      </Tooltip>
                      {!isWhiteLabel && (
                        <Tooltip
                          title={'Save'}
                          placement='bottom-start'
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                display: 'flex',
                                padding:
                                  'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                alignSelf: 'stretch',
                                borderRadius: 'var(--radius-md, 8px)',
                                background:
                                  'var(--Background-200, var(--bg-colors))',
                                color: 'var(--text-prim25)',
                                maxWidth: '700px',
                                '& .MuiTooltip-arrow': {
                                  color:
                                    'var(--Background-200, var(--bg-colors))',
                                },
                              },
                            },
                          }}
                        >
                          <div
                            className='audit-btn-star'
                            onClick={() => {
                              if (
                                cmdtDesc &&
                                HTSCode &&
                                cmdtDesc.length > 0 &&
                                HTSCode.length > 0
                              ) {
                                openTicketModal();
                              }
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                            >
                              <path
                                d='M5.83333 2.5V5.33333C5.83333 5.80004 5.83333 6.0334 5.92416 6.21166C6.00406 6.36846 6.13154 6.49594 6.28834 6.57584C6.4666 6.66667 6.69996 6.66667 7.16667 6.66667H12.8333C13.3 6.66667 13.5334 6.66667 13.7117 6.57584C13.8685 6.49594 13.9959 6.36846 14.0758 6.21166C14.1667 6.0334 14.1667 5.80004 14.1667 5.33333V3.33333M14.1667 17.5V12.1667C14.1667 11.7 14.1667 11.4666 14.0758 11.2883C13.9959 11.1315 13.8685 11.0041 13.7117 10.9242C13.5334 10.8333 13.3 10.8333 12.8333 10.8333H7.16667C6.69996 10.8333 6.4666 10.8333 6.28834 10.9242C6.13154 11.0041 6.00406 11.1315 5.92416 11.2883C5.83333 11.4666 5.83333 11.7 5.83333 12.1667V17.5M17.5 7.77124V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H12.2288C12.6364 2.5 12.8402 2.5 13.0321 2.54605C13.2021 2.58688 13.3647 2.65422 13.5138 2.7456C13.682 2.84867 13.8261 2.9928 14.1144 3.28105L16.719 5.88562C17.0072 6.17387 17.1513 6.318 17.2544 6.48619C17.3458 6.63531 17.4131 6.79789 17.4539 6.96795C17.5 7.15976 17.5 7.36358 17.5 7.77124Z'
                                stroke='var(--card-text)'
                                strokeWidth='1.66667'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  {autoTab === 'HTS Classification' && (
                    <>
                      <div className='disclaimer-text'>
                        The 10-digit HTS classification provided is for general,
                        educational and planning purposes only and not for
                        purposes of filing and entry. Unless you are the
                        importer of record, the specific tariff classification
                        to be applied to a specific entry of merchandise is to
                        be determined, supervised and controlled by the licensed
                        customs broker. Users other than the importer of record
                        must consult a licensed customs broker as classification
                        for Customs entry purposes can only be determined by a
                        licensed customs broker.
                      </div>

                      {/* Unreachable for the HTS tab since the redesigned shell
                          above now returns unconditionally for it; kept (minus the
                          removed ChatKit fallback) rather than deleting the whole
                          legacy staged-classify UI in this pass. */}
                      {!isInteractionDisabled ? (
                        <div className='bite-classification-chatkit-panel'>
                          <HtsClassificationChatClaude
                            ref={htsClassificationChatClaudeRef}
                            apiUrl={apiUrl}
                            onThreadChange={handleHtsThreadChange}
                            onNewChat={handleHtsNewChat}
                            onResponseStart={() => {
                              setChatkitThinking(true);
                              setChatkitError(null);
                            }}
                            onResponseEnd={() => setChatkitThinking(false)}
                            onError={(message) => setChatkitError(message)}
                            onLivePatch={handleHtsLivePatch}
                          />
                        </div>
                      ) : (
                        <>
                          {currentStage === 0 && (
                            <InputForm
                              onSubmit={handleSubmit}
                              isStreaming={isStreaming}
                              initialValue={userProductDesc}
                              isInteractionDisabled={isInteractionDisabled}
                            />
                          )}
                          {currentStage === 1 && (
                            <div className='classify'>
                              <div className='classify-header'>
                                Product Name / Product Description
                              </div>
                              <div className='classify-cont'>
                                <div className='classify-cont-sec'>
                                  <div className='classify-cont-sec-div'>
                                    <textarea
                                      placeholder={userProductDesc}
                                      className='classify-cont-sec-input'
                                      rows={1}
                                      onInput={(e) => {
                                        const target =
                                          e.target as HTMLTextAreaElement; // type assertion
                                        target.style.height = 'auto';
                                        target.style.height = `${target.scrollHeight}px`;
                                      }}
                                    />
                                  </div>
                                </div>
                                <div
                                  style={{
                                    marginTop: '15px',
                                    borderTop: '1px solid var(--border-color)',
                                    paddingTop: '15px',
                                    width: '100%',
                                  }}
                                >
                                  <div
                                    className='classify-header'
                                    style={{ fontSize: '12px', opacity: 0.8 }}
                                  >
                                    Optional Details (Improves Accuracy)
                                  </div>

                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      width: '100%',
                                      gap: '10px',
                                      marginTop: '10px',
                                    }}
                                  >
                                    <div style={{ width: 'inherit' }}>
                                      <label
                                        style={{
                                          fontSize: '11px',
                                          display: 'block',
                                          marginBottom: '4px',
                                          fontWeight: '600',
                                          fontFamily: 'Inter',
                                        }}
                                      >
                                        Material
                                      </label>
                                      <input
                                        className='classify-cont-sec-input'
                                        style={{ padding: '8px' }}
                                        placeholder='e.g. Steel, Cotton'
                                        value={material}
                                        onChange={(e) =>
                                          setMaterial(e.target.value)
                                        }
                                        disabled
                                      />
                                    </div>
                                    <div style={{ width: 'inherit' }}>
                                      <label
                                        style={{
                                          fontSize: '11px',
                                          display: 'block',
                                          marginBottom: '4px',
                                          fontWeight: '600',
                                          fontFamily: 'Inter',
                                        }}
                                      >
                                        Intended Use
                                      </label>
                                      <input
                                        className='classify-cont-sec-input'
                                        style={{ padding: '8px' }}
                                        placeholder='e.g. Medical, Filtration'
                                        value={intendedUse}
                                        onChange={(e) =>
                                          setIntendedUse(e.target.value)
                                        }
                                        disabled
                                      />
                                    </div>
                                    <div style={{ width: 'inherit' }}>
                                      <label
                                        style={{
                                          fontSize: '11px',
                                          display: 'block',
                                          marginBottom: '4px',
                                          fontWeight: '600',
                                          fontFamily: 'Inter',
                                        }}
                                      >
                                        Physical State
                                      </label>
                                      <input
                                        className='classify-cont-sec-input'
                                        style={{ padding: '8px' }}
                                        placeholder='e.g. Liquid, Finished'
                                        value={physicalState}
                                        onChange={(e) =>
                                          setPhysicalState(e.target.value)
                                        }
                                        disabled
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <LoadingFeedback size='small' />
                              <div className='classify-footer'>
                                <div className='classify-footer-btn-loading'>
                                  <div className='classify-footer-btn-div'>
                                    Reasoning...
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          {currentStage === 2 && (
                            <FollowupQuestionsDisplay
                              followupItems={followupItems}
                              isStreaming={isStreaming}
                              onSubmitFollowup={handleFollowupSubmit}
                              userProductDesc={userProductDesc}
                              material={material}
                              intendedUse={intendedUse}
                              physicalState={physicalState}
                            />
                          )}
                          {currentStage === 3 && (
                            <>
                              <div
                                className='hts-classification-result-summary'
                                style={{
                                  paddingTop: '10px',
                                  color:
                                    'var(--Text-Main, var(--text-primary))',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  height: '600px',
                                  maxHeight: '520px',
                                  overflow: 'auto',
                                }}
                              >
                                <div>
                                  <h2
                                    style={{
                                      color:
                                        'var(--Text-Main, var(--text-primary))',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                    }}
                                  >
                                    Product Description:{' '}
                                  </h2>
                                  {cmdtDesc && (
                                    <h2
                                      style={{
                                        color:
                                          'var(--Text-Main, var(--text-primary))',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                      }}
                                    >
                                      {cmdtDesc}
                                    </h2>
                                  )}
                                </div>

                                <div>
                                  <h2
                                    style={{
                                      color:
                                        'var(--Text-Main, var(--text-primary))',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                    }}
                                  >
                                    HTS Code:{' '}
                                  </h2>
                                  {HTSCode && (
                                    <h2
                                      style={{
                                        color:
                                          'var(--Text-Main, var(--text-primary))',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                      }}
                                    >
                                      {HTSCode}
                                    </h2>
                                  )}
                                </div>
                                <div>
                                  <h2
                                    style={{
                                      color:
                                        'var(--Text-Main, var(--text-primary))',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                    }}
                                  >
                                    Section and Chapter Notes:{' '}
                                  </h2>
                                  {sectionNotes && (
                                    <div
                                      className={`explanatory_notes ${
                                        !isExpandedSecNotes ? 'expanded' : ''
                                      }`}
                                    >
                                      {formatExplanatoryNote(sectionNotes)}
                                    </div>
                                  )}
                                  {sectionNotes && (
                                    <div
                                      className={`explanatory_notes ${
                                        !isExpandedSecNotes ? 'expanded' : ''
                                      }`}
                                    >
                                      {formatExplanatoryNote(sectionNotes)}
                                    </div>
                                  )}
                                  {sectionNotes && (
                                    <div
                                      className='expl-notes-btn'
                                      onClick={() =>
                                        setIsExpandedSecNotes(
                                          !isExpandedSecNotes,
                                        )
                                      }
                                    >
                                      {!isExpandedSecNotes
                                        ? 'Read More'
                                        : 'Read Less'}
                                    </div>
                                  )}
                                </div>

                                {/* <div>
                              <h2
                                style={{
                                  color:
                                    'var(--Text-Main, var(--text-primary))',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                }}
                              >
                                Explanatory Notes:{' '}
                              </h2>
                              {explanatoryNotes && (
                                <div
                                  className={`explanatory_notes ${
                                    !isExpanded ? 'expanded' : ''
                                  }`}
                                >
                                  {formatExplanatoryNote(explanatoryNotes)}
                                </div>
                              )}
                              {explanatoryNotes && (
                                <div
                                  className={`explanatory_notes ${
                                    !isExpanded ? 'expanded' : ''
                                  }`}
                                >
                                  {formatExplanatoryNote(explanatoryNotes)}
                                </div>
                              )}
                              {explanatoryNotes && (
                                <div
                                  className='expl-notes-btn'
                                  onClick={() => setIsExpanded(!isExpanded)}
                                  style={{ marginBottom: '10px' }}
                                >
                                  {!isExpanded ? 'Read More' : 'Read Less'}
                                </div>
                              )}
                            </div> */}
                                <div>
                                  <h2
                                    style={{
                                      color:
                                        'var(--Text-Main, var(--text-primary))',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      paddingTop: '10px',
                                    }}
                                  >
                                    CROSS Rulings:{' '}
                                  </h2>
                                  {safeCrossLinks.length > 0 && (
                                    <div className='bite-classify-cross-links'>
                                      {safeCrossLinks.map((item, index) => (
                                        <div
                                          key={`${item.url}-${index}`}
                                          className='bite-classify-cross-link-card'
                                        >
                                          <a
                                            href={item.url}
                                            rel='noopener noreferrer'
                                            target='_blank'
                                            className='bite-classify-cross-link-title'
                                          >
                                            {item.title || item.url}
                                          </a>

                                          {typeof item.relevance_percent ===
                                            'number' && (
                                            <div className='bite-classify-cross-link-relevance'>
                                              Relevance:{' '}
                                              {item.relevance_percent}%
                                            </div>
                                          )}

                                          {item.relevance_note && (
                                            <div className='bite-classify-cross-link-note'>
                                              {item.relevance_note}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {safeCrossLinks.length > 5 && (
                                    <div
                                      className='expl-notes-btn'
                                      onClick={() =>
                                        setIsExpandedCross(!isExpandedCross)
                                      }
                                    >
                                      {!isExpandedCross
                                        ? 'Read More'
                                        : 'Read Less'}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className='classify-footer'>
                                <div
                                  className='classify-footer-btn'
                                  onClick={openTicketModal}
                                >
                                  <div className='classify-footer-btn-divs'>
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='18'
                                      height='18'
                                      viewBox='0 0 18 18'
                                      fill='none'
                                    >
                                      <path
                                        d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                                        stroke='#fff'
                                        strokeWidth='1.66667'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                      />
                                    </svg>
                                    Save
                                  </div>
                                </div>
                                <div
                                  className='classify-footer-btn'
                                  onClick={handleReset}
                                >
                                  <div className='classify-footer-btn-divs'>
                                    New Search
                                  </div>
                                </div>
                                <Link
                                  to={{
                                    pathname: '/import-screening',
                                    state: {
                                      primary: HTSCode ? HTSCode : '',
                                    },
                                  }}
                                >
                                  <div className='classify-footer-btn'>
                                    <div className='classify-footer-btn-divs'>
                                      Run Screen
                                    </div>
                                  </div>
                                </Link>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {autoTab === 'Export Control Classification Number' && (
                    <>
                      <div className='disclaimer-text'>
                        Please enter your known ECCN Number and country of
                        export in order to receive relevant licensing and
                        control data for your product.{' '}
                      </div>
                      <ECCNForm
                        onSubmit={handleESubmit}
                        isStreaming={isLoading}
                        initialValue={''}
                        isInteractionDisabled={isInteractionDisabled}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className='bill-container-fit-screens-classify hts-classification-output-pane'>
                {/* Managed-agent chat only, so this always reads the shared
                    chatkitStreamData; the old branch that fell back to the
                    legacy `streamData` went with the ChatKit widget. */}
                {autoTab === 'HTS Classification' &&
                  (!chatkitThreadId ? (
                    <div className='classify-results'>
                      Enter information to see results. The more information you
                      provide, the more accurate the results will be.
                    </div>
                  ) : (
                    <div className='classify-results-card-list'>
                      <StreamDisplay
                        streamData={chatkitStreamData}
                        ticket={false}
                      />
                    </div>
                  ))}
                {autoTab === 'Export Control Classification Number' &&
                  (currentEStage === 0 ? (
                    <div className='classify-results'>
                      Enter information to see results.
                    </div>
                  ) : (
                    <div className='classify-results-card-list'>
                      <StreamEDisplay message={userECCN} ticket={isLoading} />
                    </div>
                  ))}

                <div className='classify-container-footer'>
                  <div
                    className='classify-container-footer-clear'
                    onClick={handleReset}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M15.75 7.5C15.75 7.5 14.2463 5.45116 13.0246 4.22868C11.803 3.0062 10.1148 2.25 8.25 2.25C4.52208 2.25 1.5 5.27208 1.5 9C1.5 12.7279 4.52208 15.75 8.25 15.75C11.3273 15.75 13.9237 13.6907 14.7362 10.875M15.75 7.5V3M15.75 7.5H11.25'
                        stroke='var(--card-text)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    <div className='classify-container-footer-clear-text'>
                      Clear
                    </div>
                  </div>
                  {!isWhiteLabel && (
                    <>
                      {HTSCode?.length === 0 ||
                      cmdtDesc?.length === 0 ||
                      !HTSCode ||
                      !cmdtDesc ? (
                        <div className='classify-container-footer-btn-save'>
                          <div
                            className='classify-container-footer-btn-result-text'
                            style={{ color: '#fff' }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 18 18'
                              fill='none'
                            >
                              <path
                                d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                                stroke='#fff'
                                strokeWidth='1.66667'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                            Save
                          </div>
                        </div>
                      ) : !ticketId ? (
                        <div
                          className='active-save-btn'
                          id='classify-save-id'
                          onClick={openTicketModal}
                        >
                          <div
                            className='active-text'
                            style={{ color: '#fff' }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 18 18'
                              fill='none'
                            >
                              <path
                                d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                                stroke='#fff'
                                strokeWidth='1.66667'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                            Save
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={`/monitor/ticket/${ticketId}`}
                          style={{ flex: '1 0' }}
                        >
                          <div className='active-save-btn'>
                            <div
                              className='active-text'
                              style={{ color: '#fff' }}
                            >
                              View Saved
                            </div>
                          </div>
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <DeleteCommonModal
            handleClose={() => setIsDeleteModalOpen(false)}
            title='Delete search?'
            paragraph='This will permanently remove this search from your group list.'
          />
        </Modal>
        <Modal open={isModalOpen} onClose={handleCloseModal}>
          <CreateTicketWizard
            cur_user={cur_user}
            handleSaveTicket={saveTicket}
            newTicketName={newTicketName}
            setNewTicketName={setNewTicketName}
            shareEmail={shareEmail}
            setShareEmail={setShareEmail}
            savingTicket={savingTicket}
          />
        </Modal>
        {renderLeaveModal()}
      </section>
    </>
  );
};

export default Classification;
